import type { TFunction } from 'i18next';
import type {
  ContentTypeDefinition,
  ContentTypePropertyConfig,
  ContentTypePropertySchema,
} from '../../../lib/flotiq-client/types/api-response';
import type { ContentTypeFormProperty } from '../../PropertyModalForm/helpers/parseProperty';

import { use, useCallback, useMemo, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { findIcon } from '../../../lib/ctdIcons';
import { formatErrorToString, getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import Card from '../../../components/Card/Card';
import Tooltip from '../../../components/Tooltip/Tooltip';

import ContentTypeFormContext from '../../../contexts/ContentTypeFormContext';
import ListPropertiesContext from '../../../contexts/ListPropertiesContext';
import PropertyModalCtdsContext from '../../../contexts/PropertyModalCtdsContext';

import {
  ArrowUpThinIcon,
  CopyIcon,
  DeleteIcon,
  PencilIcon,
} from '../../../images/shapes';

import { withFieldGroup } from '../../../form-hooks/form';
import ListProperties from '../ListProperties/ListProperties';

const generateDescription = (
  t: TFunction,
  fieldProps?: ContentTypePropertyConfig,
  isRequired?: boolean,
) => {
  if (!fieldProps) return '';
  const items = [];
  if (isRequired) items.push(t('ContentTypeForm.Descriptions.Required'));
  if (fieldProps.unique) items.push(t('ContentTypeForm.Descriptions.Unique'));
  if (fieldProps.isPassword)
    items.push(t('ContentTypeForm.Descriptions.Password'));
  if (fieldProps.inputType === 'dateTime' && fieldProps.showTime)
    items.push(t('ContentTypeForm.Descriptions.ShowTimePicker'));
  if (fieldProps.readonly)
    items.push(t('ContentTypeForm.Descriptions.Readonly'));
  if (fieldProps.isTitlePart)
    items.push(t('ContentTypeForm.Descriptions.PartOfTitle'));
  if (fieldProps.hidden) items.push(t('ContentTypeForm.Descriptions.Hidden'));
  if (
    (fieldProps.inputType === 'datasource' &&
      fieldProps.validation?.relationMultiple) ||
    (fieldProps.inputType === 'select' && fieldProps.multiple)
  )
    items.push(t('ContentTypeForm.Descriptions.Multiple'));
  if (fieldProps.inputType === 'datasource') {
    const relationType = fieldProps.validation?.relationContenttype;
    const relationLabel =
      relationType === '_media'
        ? t('ContentTypeForm.Descriptions.Media')
        : relationType;
    items.push(
      t('ContentTypeForm.Descriptions.Relation', {
        to: relationLabel
          ? relationLabel
          : t('ContentTypeForm.Descriptions.AnyType'),
      }),
    );
  }
  if (fieldProps.inputType === 'select' && fieldProps.useOptionsWithLabels)
    items.push(t('ContentTypeForm.Descriptions.LabelOptions'));
  if (!items.length) return;
  return items.join(' • ');
};

export type DraggableCardEditFn = (
  fieldName?: string,
  fieldProps?: ContentTypePropertyConfig,
  fieldSchema?: ContentTypePropertySchema,
  isRequired?: boolean,
  idx?: number,
  parentInputType?: string,
) => void;

type DraggableCardProps = {
  /**
   * The name of the field represented by the card.
   */
  fieldName: string;
  /**
   * The index of the card in the current order.
   */
  idx: number;
  /**
   * The current order of the ctd's properties.
   */
  currentOrder: string[];
  /**
   * On drop callback
   */
  onDrop: (draggedFieldName: string, targetFieldName: string) => void;
  /**
   * On delete callback
   */
  onDelete: (fieldName: string) => void;
  /**
   * On edit callback
   */
  onEdit: DraggableCardEditFn;
  /**
   * On move up callback
   */
  onUp: (idx: number) => void;
  /**
   * On move down callback
   */
  onDown: (idx: number) => void;
  /**
   * On duplicate callback
   */
  onDuplicate: DraggableCardEditFn;
  /**
   * The config properties of the field.
   */
  fieldProps?: ContentTypePropertyConfig;
  /**
   * The schema properties of the field
   */
  fieldSchema?: ContentTypePropertySchema;
  /**
   * Whether the field is required.
   */
  isRequired?: boolean;
  /**
   * The name of the field that is parent for this field. Used for lists
   */
  parentInputType?: string;
  /**
   * Additional container classes for the card.
   */
  additionalContainerClasses?: string;
  /**
   * The test ID for the card.
   */
  testId?: string;
};

const DraggableCard = withFieldGroup({
  defaultValues: {} as
    | {
        metaDefinition: ContentTypeDefinition['metaDefinition'];
        schemaDefinition: ContentTypeDefinition['schemaDefinition'];
      }
    | ContentTypeFormProperty,
  props: {} as DraggableCardProps,
  render: function Render({
    group,
    fieldName,
    fieldProps,
    fieldSchema,
    isRequired,
    idx,
    currentOrder,
    onDrop,
    onDelete,
    onEdit,
    onUp,
    onDown,
    onDuplicate,
    parentInputType,
    additionalContainerClasses = '',
    testId = '',
  }) {
    const ref = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const inputType = useMemo(() => fieldProps?.inputType, [fieldProps]);
    const { propertiesErrors, disabled } = use(ContentTypeFormContext);
    const { highListName } = use(ListPropertiesContext);

    const propertyContext = use(PropertyModalCtdsContext);
    const isInPropertyModal = useMemo(
      () => Object.keys(propertyContext).length > 0,
      [propertyContext],
    );

    const [propertyErrors, propertyGlobalErrors] = useMemo(
      () => [propertiesErrors[fieldName], propertiesErrors[fieldName]?.global],
      [propertiesErrors, fieldName],
    );

    const dragAndDropType = highListName ? `${highListName}-card` : 'card';

    const [, drop] = useDrop<{ fieldName: string; idx: number }>({
      accept: dragAndDropType,
      canDrop: () => true,
      hover: (item, monitor) => {
        if (!ref.current) {
          return;
        }
        if (monitor.canDrop() && !monitor.didDrop() && !monitor.isOver()) {
          onDrop(item.fieldName, fieldName);
        }
      },
    });

    const [{ cardIsDragging, anyCardIsDragging }, drag] = useDrag({
      item: { fieldName, idx },
      type: dragAndDropType,
      canDrag: () => !disabled,
      collect: (monitor) => {
        return {
          cardIsDragging: monitor.isDragging(),
          anyCardIsDragging: !!monitor.getItem(),
        };
      },
    });

    drag(drop(ref));

    const handleDelete = useCallback(
      () => onDelete(fieldName),
      [fieldName, onDelete],
    );

    const handleEdit = useCallback(
      () =>
        onEdit(
          fieldName,
          fieldProps,
          fieldSchema,
          isRequired,
          undefined,
          parentInputType,
        ),
      [fieldName, fieldProps, fieldSchema, isRequired, onEdit, parentInputType],
    );

    const handleOrderUp = useCallback(() => onUp(idx), [idx, onUp]);

    const handleOrderDown = useCallback(() => onDown(idx), [idx, onDown]);

    const handleDuplicate = useCallback(
      () => onDuplicate(fieldName, fieldProps, fieldSchema, isRequired, idx),
      [fieldName, fieldProps, fieldSchema, idx, isRequired, onDuplicate],
    );

    const defaultIconClasses = 'h-3.5 text-slate-700 opacity-50';

    return (
      <div>
        <div
          className={twMerge(
            'bg-slate-50 dark:bg-slate-800 w-full rounded-lg border border-slate-200 dark:border-slate-800',
            'duration-250 transition ease-in-out',
            !disabled && 'cursor-grab',
            cardIsDragging && 'opacity-50 cursor-grabbing! shadow-lg ',
            !(anyCardIsDragging && !cardIsDragging) && 'hover:shadow-lg',
            propertyErrors && 'border border-red',
            additionalContainerClasses,
          )}
          ref={ref}
        >
          <Card
            thumbnail={findIcon(fieldProps)}
            body={
              <div className="flex flex-wrap sm:flex-nowrap pl-3 text-slate-400 h-full justify-between items-center">
                <div className="overflow-hidden max-w-fit grid">
                  <p
                    className={twMerge(
                      'text-slate-900 dark:text-slate-200 text-lg font-medium leading-5',
                      'line-clamp-1 py-0.5 break-all pr-2',
                    )}
                    {...getTestProps(testId, 'card-label')}
                  >
                    {fieldProps?.label}
                  </p>
                  <div
                    className={'mt-1 empty:hidden'}
                    {...getTestProps(testId, 'card-description')}
                  >
                    {generateDescription(t, fieldProps, isRequired)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {currentOrder.length > 1 && (
                    <div className="inline-flex items-center dark:text-slate-200 gap-2">
                      <Button
                        onClick={handleOrderUp}
                        iconImage={
                          <ArrowUpThinIcon
                            className={twMerge(
                              'dark:text-slate-50',
                              defaultIconClasses,
                              !(disabled || idx === 0) && 'hover:opacity-100',
                            )}
                          />
                        }
                        buttonColor="borderless"
                        disabled={disabled || idx === 0}
                        additionalClasses="p-1"
                        {...getTestProps(
                          testId,
                          `${fieldName}-up-icon`,
                          'testId',
                        )}
                      />
                      <Button
                        onClick={handleOrderDown}
                        iconImage={
                          <ArrowUpThinIcon
                            className={twMerge(
                              'rotate-180 dark:text-slate-50',
                              defaultIconClasses,
                              !(disabled || idx === currentOrder.length - 1) &&
                                'hover:opacity-100',
                            )}
                          />
                        }
                        buttonColor="borderless"
                        disabled={disabled || idx === currentOrder.length - 1}
                        additionalClasses="p-1"
                        {...getTestProps(
                          testId,
                          `${fieldName}-down-icon`,
                          'testId',
                        )}
                      />
                    </div>
                  )}
                  <div className="inline-flex items-center gap-2">
                    <Button
                      onClick={handleEdit}
                      iconImage={
                        <PencilIcon
                          className={twMerge(
                            'dark:text-slate-50',
                            defaultIconClasses,
                            !disabled && 'hover:opacity-100',
                          )}
                        />
                      }
                      buttonColor="borderless"
                      disabled={disabled}
                      additionalClasses="p-1"
                      {...getTestProps(
                        testId,
                        `${fieldName}-edit-icon`,
                        'testId',
                      )}
                    />
                    <Button
                      onClick={handleDelete}
                      iconImage={
                        <DeleteIcon
                          className={twMerge(
                            'dark:text-red-300',
                            defaultIconClasses,
                            !disabled && 'hover:opacity-100',
                          )}
                        />
                      }
                      buttonColor="borderless"
                      disabled={disabled}
                      additionalClasses="p-1"
                      {...getTestProps(
                        testId,
                        `${fieldName}-delete-icon`,
                        'testId',
                      )}
                    />
                    <Tooltip tooltip={t('ContentTypeForm.DuplicateProperty')}>
                      <Button
                        onClick={handleDuplicate}
                        iconImage={
                          <CopyIcon
                            className={twMerge(
                              'dark:text-slate-50',
                              defaultIconClasses,
                              !disabled && 'hover:opacity-100',
                            )}
                          />
                        }
                        buttonColor="borderless"
                        disabled={disabled}
                        additionalClasses="p-1"
                        {...getTestProps(
                          testId,
                          `${fieldName}-duplicate-icon`,
                          'testId',
                        )}
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            }
            darkenOnHover={false}
            additionalContainerClasses={twMerge(
              'bg-transparent w-full p-4 pr-7 overflow-visible shadow-none!',
            )}
            additionalImageContainerClasses="w-fit min-w-fit rounded-sm"
            additionalBodyClasses="w-full"
            additionalThumbnailClasses="w-fit"
            horizontal
            {...getTestProps(testId, fieldName, 'testId')}
          />

          {inputType === 'object' && (
            <ListProperties
              form={group}
              fields={
                isInPropertyModal
                  ? {
                      key: 'key',
                      required: 'required',
                      config: 'config',
                      schema: 'schema',
                    }
                  : {
                      metaDefinition: 'metaDefinition',
                      schemaDefinition: 'schemaDefinition',
                    }
              }
              listName={fieldName}
              additionalClasses="dark:bg-slate-950"
              listPropertiesItems={fieldProps?.items}
              listSchemaItems={fieldSchema?.items}
              parentInputType={
                parentInputType ? `${parentInputType}_${inputType}` : inputType
              }
              isOpen
              testId="testId"
              {...getTestProps(testId, `${fieldName}-properties`, 'testId')}
            />
          )}
        </div>
        {propertyGlobalErrors?.length > 0 && (
          <div
            className="text-red text-sm mb-2"
            {...getTestProps(testId, 'global-prop-error')}
          >
            {formatErrorToString(propertyGlobalErrors)}
          </div>
        )}
      </div>
    );
  },
});

export default DraggableCard;
